import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Box,
  Separator,
  Text,
} from "@salestrak/ui";

import type { FilterConfig, UseFiltersResult } from "#app/lib/filters/types";
import { FilterValueInput } from "./filter-value-input";

type QuickFiltersProps = {
  config: FilterConfig;
  filters: UseFiltersResult;
};

/**
 * Simple tab content: renders each quick filter as an accordion section.
 *
 * Each section contains a FilterValueInput with the quick filter's fixed
 * operator. Changes flow through `filters.setQuickValue` which updates
 * blocks[0] of the shared FilterState.
 */
export function QuickFilters({ config, filters }: QuickFiltersProps) {
  if (!config.quickFilters || config.quickFilters.length === 0) {
    return (
      <Box padding="lg">
        <Text size="sm" variant="muted">
          No quick filters configured for this view.
        </Text>
      </Box>
    );
  }

  // All accordion items default open
  const defaultValues = config.quickFilters.map((qf) => qf.field);

  return (
    <Box>
      {/* The accordion panel animates its own height in CSS and no call site
          can turn that off. Trigger and content padding are class recipes. */}
      <Accordion multiple defaultValue={defaultValues}>
        {config.quickFilters.map((qf, index) => (
          <Box key={qf.field}>
            {index > 0 && <Separator />}
            <Box paddingX="lg" paddingTop="md" paddingBottom="md">
              <AccordionItem value={qf.field} className="border-b-0">
                <AccordionTrigger className="py-0">
                  <Text weight="medium">{qf.label}</Text>
                </AccordionTrigger>
                <AccordionPanel className="pb-0">
                  <Box paddingY="sm">
                    <FilterValueInput
                      fieldType={qf.type}
                      value={filters.quickValues[qf.field]}
                      onChange={(val) => filters.setQuickValue(qf.field, val)}
                      icon={qf.icon}
                    />
                  </Box>
                </AccordionPanel>
              </AccordionItem>
            </Box>
          </Box>
        ))}
      </Accordion>
    </Box>
  );
}
